import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { ref, onValue } from 'firebase/database';
import { db } from 'utils/firebase-config';
import Styles from './ArticlesSection.module.scss';
import articlesIcon from 'assets/svg/articles-header-icon.svg';
import articlesPreviousIcon from 'assets/svg/articles-previous-icon.svg';
import articlesNextIcon from 'assets/svg/articles-next-icon.svg';
import { Article, ARTICLES_PAGE_PATH, isArticleNew } from 'views/articles/articleTypes';
import { fetchArticleBatch } from 'views/articles/fetchArticles';
import { useDateTimeFormat } from 'hooks/useDateTimeFormat';
import { formatArticleDisplayDate } from 'helpers/dateTimeFormat';

const PER_PAGE = 4;
const FETCH_LIMIT = 24;

interface ArticlesSectionProps {
  /** Marketing Home: hide unread styling/badges (API still works for guests). */
  publicMode?: boolean;
}

const ArticlesSection: React.FC<ArticlesSectionProps> = ({ publicMode = false }) => {
  const history = useHistory();
  const { setting: dateTimeSetting } = useDateTimeFormat();
  const [articles, setArticles] = useState<Article[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [page, setPage] = useState(0);
  const [direction, setDirection] = useState<'next' | 'prev'>('next');

  const loadArticles = useCallback(() => {
    fetchArticleBatch(0, FETCH_LIMIT)
      .then(({ articles: list, unreadCount: count }) => {
        setArticles(list);
        setUnreadCount(publicMode ? 0 : count);
      })
      .catch(() => {});
  }, [publicMode]);

  useEffect(() => {
    loadArticles();
  }, [loadArticles]);

  // Real-time: refetch when the backend bumps the article signal (no toast).
  useEffect(() => {
    const signalRef = ref(db, 'articleUpdates/lastPublishedAt');
    let first = true;
    const unsubscribe = onValue(signalRef, () => {
      if (first) {
        first = false;
        return;
      }
      loadArticles();
    });
    return () => unsubscribe();
  }, [loadArticles]);

  const totalPages = Math.max(1, Math.ceil(articles.length / PER_PAGE));
  const visible = articles.slice(page * PER_PAGE, page * PER_PAGE + PER_PAGE);

  const goToArticlesPage = () => {
    history.push(ARTICLES_PAGE_PATH);
  };

  const goToArticle = (id: number) => {
    history.push(`/articles/${id}`);
  };

  const showUnreadBadge = !publicMode && unreadCount > 0;

  return (
    <div className={Styles.wrapper}>
      <div className={Styles.header}>
        <div
          className={`${Styles.headerLeft} ${Styles.headerLeftClickable}`}
          onClick={goToArticlesPage}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              goToArticlesPage();
            }
          }}
          role="button"
          tabIndex={0}
          aria-label="View all Moderate Politics articles and information"
        >
          <img src={articlesIcon} alt="" className={Styles.headerIcon} />
          <span>MODERATE POLITICS ARTICLES &amp; INFORMATION</span>
          {showUnreadBadge && <span className={Styles.countBadge}>{unreadCount}</span>}
        </div>
        <div className={Styles.headerRight}>
          <button type="button" className={Styles.viewAll} onClick={goToArticlesPage}>
            View All
          </button>
          <div className={Styles.navButtons}>
            <button
              className={Styles.navBtn}
              onClick={() => {
                setDirection('prev');
                setPage(p => Math.max(0, p - 1));
              }}
              disabled={page === 0}
              aria-label="Previous articles"
              type="button"
            >
              <img src={articlesPreviousIcon} alt="" className={Styles.navIcon} />
            </button>
            <button
              className={Styles.navBtn}
              onClick={() => {
                setDirection('next');
                setPage(p => Math.min(totalPages - 1, p + 1));
              }}
              disabled={page >= totalPages - 1}
              aria-label="Next articles"
              type="button"
            >
              <img src={articlesNextIcon} alt="" className={Styles.navIcon} />
            </button>
          </div>
        </div>
      </div>

      <div
        key={page}
        className={`${Styles.cardsGrid} ${
          direction === 'next' ? Styles.cardsGridNext : Styles.cardsGridPrev
        }`}
      >
        {visible.map(article => {
          const isUnread = !publicMode && !article.isRead;
          return (
            <div
              className={`${Styles.card} ${isUnread ? Styles.unread : ''}`}
              key={article.id}
              onClick={() => goToArticle(article.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  goToArticle(article.id);
                }
              }}
              role="button"
              tabIndex={0}
              aria-label={`Read article: ${article.title}`}
            >
              {isArticleNew(article) && <span className={Styles.newRibbon}>NEW</span>}
              <div className={Styles.cardImage}>
                {article.heroImage && <img src={article.heroImage} alt="" />}
              </div>
              <div className={Styles.cardBody}>
                <p className={Styles.cardTitle}>{article.title}</p>
                <p className={Styles.cardMeta}>
                  {formatArticleDisplayDate(article, dateTimeSetting)}
                  &nbsp;&bull;&nbsp;{article.readTime}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ArticlesSection;
